import React, { useMemo, useState } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import {
    DA_Title,
    DA_TitleTag,
} from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { SSH_Buttons } from "../../../../components/molecules/Buttons/Buttons.component";
import { SSH_ImageViewer } from "../../../../components/molecules/ImageViewer/ImageViewer.component";
import { useBackgroundColor } from "../../../../hooks/useBackgroundColor/useBackgroundColor.hook";
import { usePadding } from "../../../../hooks/usePadding/usePadding.hook";
import VideoPlayerViewer from "../../../../components/organisms/VideoPlayerViewer/VideoPlayerViewer.component";
import { V1Props } from "./HeroV1.interface";
// eslint-disable-next-line css-modules/no-unused-class
import S from "./HeroV1.module.scss";

const V1 = (props: V1Props) => {
    const { backgroundColorClass, alternativeButtonTheme, hasBackgroundColor } = useBackgroundColor(
        props.backgroundColor
    );
    const { paddingClass } = usePadding(props.isCompact, hasBackgroundColor);
    const [headlineTag] = useState<string>(props["headlineTag"] || "");

    const layoutClass = useMemo(() => {
        // Image Cover w/black filter
        if (
            !props.position &&
            props.image?.url &&
            (props.filter === "FILTER_BLACK_BACKGROUND" || !props.filter)
        ) {
            return S.layoutCoverBlackFilter;
        }

        // Image Cover w/white filter
        else if (
            !props.position &&
            props.image?.url &&
            props.filter === "FILTER_WHITE_BACKGROUND"
        ) {
            return S.layoutCoverWhiteFilter;
        }

        // Video Cover w/black filter
        else if (
            !props.position &&
            props.video?.url &&
            (props.filter === "FILTER_BLACK_BACKGROUND" || !props.filter)
        ) {
            return S.layoutCoverBlackFilter;
        }

        // Video Cover w/white filter
        else if (
            !props.position &&
            props.video?.url &&
            props.filter === "FILTER_WHITE_BACKGROUND"
        ) {
            return S.layoutCoverWhiteFilter;
        }

        // Image Left
        else if (props.position === "POSITION_IMAGE_LEFT" && props.image?.url) {
            return S.heroImageLeft;
        }

        // Image Right
        else if (props.position === "POSITION_IMAGE_RIGHT" && props.image?.url) {
            return S.heroImageRight;
        }

        // VIDEO Left
        else if (props.position === "POSITION_IMAGE_LEFT" && props.video?.url) {
            return S.heroImageLeft;
        }

        // VIDEO Right
        else if (props.position === "POSITION_IMAGE_RIGHT" && props.video?.url) {
            return S.heroImageRight;
        }

        // No Image
        else {
            return S.heroText;
        }
    }, [props.filter, props.image?.url, props.position, props.video?.url]);

    // Mappers
    const heroAlternativeButtonTheme = useMemo(() => {
        // Filter: Black Background
        if (
            !props.position &&
            props.image?.url &&
            (props.filter === "FILTER_BLACK_BACKGROUND" || !props.filter)
        ) {
            return "PARTIAL_WHITE";
        }

        return alternativeButtonTheme;
    }, [props.filter, props.image?.url, props.position, alternativeButtonTheme]);

    //Just add the thinHero class if no image from Umbraco (image) or digizuiteImage
    const thinHeroClass = props?.thin === "THIN_HERO" && S.thinHero;
    const propsClassName = props?.className && S[props.className];
    const oneColumn = props.columns === 1;

    return (
        <div
            className={cn(
                S.wrapper,
                propsClassName,
                layoutClass,
                paddingClass,
                backgroundColorClass
            )}
        >
            <DA_Container noVerticalPadding className={S.container}>
                <div className={cn(S.inner, thinHeroClass, oneColumn && S.oneColumnPadding)}>
                    {/** Video */}
                    {props.video && props.video?.alias ? (
                        <div className={S.videoWrapper}>
                            <VideoPlayerViewer
                                className={S.video}
                                video={{
                                    alias: props.video?.alias,
                                    url: props.video?.url,
                                    autoplay: true,
                                    controls: false,
                                }}
                            />
                        </div>
                    ) : (
                        <div
                            className={cn(S.imageWrapper, !props.image?.url && S.imageWrapperFull)}
                        >
                            {/** Image  */}
                            {props.image?.url && (
                                <SSH_ImageViewer
                                    src={props.image?.url}
                                    alternative={props.alternative}
                                    aspectRatioX={props.image?.width}
                                    aspectRatioY={props.image?.height}
                                    className={S.image}
                                    lazy={false}
                                />
                            )}
                        </div>
                    )}

                    {/** Content */}
                    <div
                        className={cn(
                            S.contentWrapper,
                            headlineTag === "h1" ? S.contentWrapperH1 : S.contentWrapperH2
                        )}
                    >
                        <div className={S.content}>
                            {/* Logo */}
                            {props.logo ? (
                                <SSH_ImageViewer
                                    src={props.logo.url}
                                    aspectRatioX={props.logo.width}
                                    aspectRatioY={props.logo.height}
                                    className={S.logo}
                                    lazy={false}
                                />
                            ) : null}

                            {/** Tagline */}
                            {props.tagline ? (
                                <span className={S.tagline}>{props.tagline}</span>
                            ) : null}

                            {/** Title */}
                            {props.headline ? (
                                headlineTag === "h1" ? (
                                    <DA_Title h1 tag={DA_TitleTag.H1} noMargin fullWidth>
                                        {props.headline}
                                    </DA_Title>
                                ) : (
                                    <DA_Title h2 tag={DA_TitleTag.H2} noMargin fullWidth>
                                        {props.headline}
                                    </DA_Title>
                                )
                            ) : null}

                            {/** Text */}
                            {props.text ? <DA_Text html={props.text} noPadding /> : null}

                            {/** Buttons */}
                            <SSH_Buttons
                                buttons={props.buttons}
                                theme={heroAlternativeButtonTheme}
                            />
                        </div>
                    </div>
                </div>
            </DA_Container>
        </div>
    );
};

export default V1;
